import React from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { FileText, AlertTriangle, CalendarClock } from 'lucide-react'

interface Deadline {
  id: string
  title: string
  module: string
  dueDate: string
  daysRemaining: number
  status?: string
}

interface DeadlinesListProps {
  deadlines: Deadline[]
  projectId: string
}

const ModuleIcon: React.FC<{ module: string }> = ({ module }) => {
  if (module === 'SNAG') return <AlertTriangle className="h-4 w-4 text-orange-500" />
  return <FileText className="h-4 w-4 text-blue-500" />
}

const getDeadlineLink = (d: Deadline, projectId: string): string => {
  if (d.module === 'TENDER') return `/project/${projectId}/tendering/tenders/${d.id}`
  if (d.module === 'SNAG') return `/project-setup/${projectId}/snags`
  return '#'
}

const formatDaysRemaining = (
  days: number,
  t: (key: string, fallback?: string, options?: Record<string, unknown>) => string,
) => {
  if (days === 0) return t('Dashboard_deadlines_today', 'Today')
  if (days === 1) return t('Dashboard_deadlines_tomorrow', 'Tomorrow')
  // `count` as an option, not a manual replace: Czech needs three forms and
  // i18next only selects one when it is given the number (Freelo #31959948).
  return t('Dashboard_deadlines_days_remaining', '{{count}} days', { count: days })
}

const DeadlinesList: React.FC<DeadlinesListProps> = ({ deadlines, projectId }) => {
  const { t } = useTranslation()

  return (
    <Card className="h-full">
      <CardHeader className="pb-2">
        <div className="flex items-center gap-2">
          <CalendarClock className="h-4 w-4 text-muted-foreground" />
          <CardTitle className="text-sm font-medium">{t('Dashboard_deadlines_title', 'Upcoming Deadlines')}</CardTitle>
        </div>
      </CardHeader>
      <CardContent>
        {deadlines.length === 0 ? (
          <p className="text-sm text-muted-foreground py-4 text-center">
            {t('Dashboard_no_deadlines', 'No upcoming deadlines')}
          </p>
        ) : (
          <div className="space-y-0.5">
            {deadlines.map((d) => (
              <Link
                key={d.id}
                to={getDeadlineLink(d, projectId)}
                className="flex items-center gap-3 py-1.5 rounded-md -mx-2 px-2 hover:bg-muted/50 transition-colors"
              >
                <ModuleIcon module={d.module} />
                <span className="text-sm flex-1 truncate">{d.title}</span>
                <Badge
                  variant={d.daysRemaining <= 3 ? 'destructive' : 'secondary'}
                  className="text-xs whitespace-nowrap"
                >
                  {formatDaysRemaining(d.daysRemaining, t)}
                </Badge>
              </Link>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  )
}

export default DeadlinesList
